import { ConversationInfo, MessageInfo, MessageType, SessionType, GetMessagesParams } from '@/api/types/chat'
import { formatHumanTime } from '@/utils/date-utils'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  TextMessage,
  ImageMessage,
  VoiceMessage,
  VideoMessage,
  FileMessage,
  ContactMessage,
  SystemMessage
} from './_components/MessageTypes'
import { useChatActions } from '@/hooks/useChatActions'
import { useChatStore } from '@/stores/useChatStore'
import { MessageManager, TextParams, ImageParams, FileParams, TextAtParams } from '@/utils/ably-im/message-manager'
import { tabStateManager } from '@/utils/tabStateManager'
import { useFileMedia } from '@/hooks/useFileMedia'
import ToastUtils from '@/utils/toast-utils'
import { logPrint } from '@/utils/my-utils'
import { useGroupActions } from '@/hooks/useGroupActions'
import GroupSettings from './_components/settings/GroupSettings'
import PinnedMessage from './_components/message/PinnedMessage'
import MessageContextMenu from './_components/MessageContextMenu'
import { toast } from 'react-hot-toast'
import iconGroupSettings from '@/assets/images/ic_more.png'
import iconTranslate from '@/assets/images/ic_tran.png'
import { useTranslation } from '@/hooks/useTranslation'
import TranslationSettingsDialog from './_components/message/TranslationSettingsDialog'
import AtSelectorPopup from './_components/message/AtSelectorPopup'
import { GroupInfo, GroupMember } from '@/api/types'
import { useGroupStore } from '@/stores/useGroupStore'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import { groupPermissionUtil } from '@/utils/permission-group-utils'

// @ 用户的联合类型定义
type AtUser = GroupMember | { id: 'all'; nickname: '所有人'; avatar: ''; handle: 0; role: 'member' }


interface ChatMessageProps {
  conversation: ConversationInfo
}

/**
 * 渲染消息内容的组件
 */
const MessageContent: React.FC<{ message: MessageInfo; isOwnMessage: boolean }> = ({
  message,
  isOwnMessage
}) => {

  const messageType = (message.messageType ?? message.decryptedContentData?.messageType) as MessageType;

  // 系统消息类型（不显示在聊天气泡中）
  const isSystemMessage = messageType?.includes("GROUP") ?? false;

  if (isSystemMessage) {
    return <SystemMessage message={message} />;
  }

  // 根据消息类型选择对应的组件
  switch (messageType) {
    case MessageType.IMAGE:
      return <ImageMessage message={message} isOwnMessage={isOwnMessage} />;

    case MessageType.VOICE:
      return <VoiceMessage message={message} isOwnMessage={isOwnMessage} />;

    case MessageType.VIDEO:
      return <VideoMessage message={message} isOwnMessage={isOwnMessage} />;

    case MessageType.FILE:
      return <FileMessage message={message} isOwnMessage={isOwnMessage} />;

    case MessageType.CONTACT:
      return <ContactMessage message={message} isOwnMessage={isOwnMessage} />;

    case MessageType.TEXT:
    case MessageType.AT:
    default:
      return <TextMessage message={message} isOwnMessage={isOwnMessage} />;
  }
};

export default function ChatMessageView({
  conversation
}: ChatMessageProps): React.JSX.Element {
  const [messageText, setMessageText] = useState('')
  const isUploadingFile = useRef(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // 加载更多消息的状态
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)

  // 群组设置侧边栏状态
  const [showGroupSettings, setShowGroupSettings] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    messageId: string
    position: { x: number; y: number }
  } | null>(null)

  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null)

  const { getGroupInfo } = useGroupActions()
  // 群组信息
  const groupInfoMap = useGroupStore((state) => state.groupInfoMap)
  const isOwner = groupInfo?.owner_id === tabStateManager.getUserId()

  // 翻译设置对话框状态
  const [showTranslationSettings, setShowTranslationSettings] = useState(false)

  // Emoji 相关状态
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [emojiButtonPosition, setEmojiButtonPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  // @ 功能相关状态
  const [showAtSelector, setShowAtSelector] = useState(false)
  const [atSearchTerm, setAtSearchTerm] = useState('')
  const [atInputPosition, setAtInputPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [selectedAtUsers, setSelectedAtUsers] = useState<AtUser[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const emojiButtonRef = useRef<HTMLButtonElement>(null)

  logPrint("ChatMessageView ==", groupInfo);

  const {
    getMessages,
    isMessagesLoading,
    loadMoreMessages: loadMoreMessagesAction,
    ackMessages,
    pinGroupMessage,
  } = useChatActions()

  const messages = useChatStore((state: any) => state.messages)
  const messagesLastEvaluatedKey = useRef<string | null>(null)

  // 使用文件媒体管理 hook
  const {
    selectAndUploadImageWithAttachment,
    selectAndUploadDocumentWithAttachment,
  } = useFileMedia()

  // 使用翻译功能 hook
  const { translateText, autoTranslateMessage } = useTranslation()

  // 获取翻译设置
  const getTranslationSettings = useChatStore((state: any) => state.getTranslationSettings)

  // 获取当前会话的消息
  const currentMessages = conversation ? messages[conversation.id] || [] : []

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView()
  }

  // 统一的权限检查函数
  const checkPermission = (permissionType: 'speak' | 'image' | 'file' | 'member') => {
    // 如果不是群组聊天，直接允许
    if (conversation.session_type !== SessionType.Group) return true

    // 如果没有权限字符串，默认允许
    if (!groupInfo?.permissions) return true

    // 群主和管理员可以绕过权限限制
    if (isOwner) return true

    // 普通成员根据权限字符串检查
    switch (permissionType) {
      case 'speak':
        return groupPermissionUtil.canMemberSpeak(groupInfo?.permissions)
      case 'image':
        return groupPermissionUtil.canSendImage(groupInfo?.permissions)
      case 'file':
        return groupPermissionUtil.canSendFile(groupInfo?.permissions)
      case 'member':
        return groupPermissionUtil.canViewMemberProfile(groupInfo?.permissions)
      default:
        return false
    }
  }

  // 计算各种权限
  const canSendMessage = checkPermission('speak')
  const canSendImage = checkPermission('image')
  const canSendFile = checkPermission('file')

  // 加载更多历史消息
  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (!conversation?.id || isLoadingMore || !hasMoreMessages) {
      return
    }

    // 获取当前会话的 last_evaluated_key
    const lastKey = messagesLastEvaluatedKey.current
    if (!lastKey) {
      console.log('没有更多历史消息')
      setHasMoreMessages(false)
      return
    }

    setIsLoadingMore(true)

    // 记录当前滚动位置，用于保持滚动位置
    const container = messagesContainerRef.current
    const previousScrollHeight = container?.scrollHeight || 0
    const previousScrollTop = container?.scrollTop || 0

    try {
      const params: GetMessagesParams = {
        limit: 50,
        last_evaluated_key: lastKey
      }

      // 使用 hook 中的 loadMoreMessages 方法
      const result = await loadMoreMessagesAction(conversation.id, params)

      if (result && result.data.length > 0) {
        // 如果返回的消息数量少于请求数量，说明没有更多消息了
        if (result.data.length < (params.limit || 50)) {
          setHasMoreMessages(false)
        }

        messagesLastEvaluatedKey.current = result.last_evaluated_key ?? null

        // 保持滚动位置，防止跳动
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            const heightDifference = newScrollHeight - previousScrollHeight
            container.scrollTop = previousScrollTop + heightDifference
          }
        }, 0)

      } else {
        console.log('没有更多历史消息')
        setHasMoreMessages(false)
      }
    } catch (error) {
      console.error('加载历史消息失败:', error)
      ToastUtils.showToastFail('加载历史消息失败')
    } finally {
      setIsLoadingMore(false)
    }
  }, [conversation?.id, isLoadingMore, hasMoreMessages, messagesLastEvaluatedKey, loadMoreMessagesAction])

  // 监听会话变化，加载初始消息
  useEffect(() => {
    if (!conversation?.id) return

    initData();
  }, [conversation?.id])

  const initData = async () => {
    // 只在切换会话时加载消息，新消息由 MessageManager 直接添加到 store
    const result = await getMessages(conversation.id)
    if (result) {
      messagesLastEvaluatedKey.current = result?.last_evaluated_key ?? null

      // 将消息已读
      const conversationId = conversation.id ?? result.data[0].conversation_id;
      const myId = tabStateManager.getUserId();
      const messageIds = result.data
        .filter(message => message.sender_id !== myId && message.ack === false)
        .map(message => message.message_id)
      if (messageIds && messageIds.length > 0) {
        ackMessages(conversationId, messageIds, conversation.session_type)
      }
    }

    // 如果是群组，加载群组信息
    if (conversation.session_type === SessionType.Group) {
      const groupId = useGroupStore.getState().groupMap.get(conversation.id)?.id
      if (groupId) {
        const groupInfo = await getGroupInfo(groupId)
        if (groupInfo) setGroupInfo(groupInfo)
      }
    }

    // 重置加载更多状态
    setHasMoreMessages(true)
  }

  useEffect(() => {
    const groupInfo = useGroupStore.getState().getGroupInfo({ conversationId: conversation.id })
    if (groupInfo) setGroupInfo(groupInfo)
  }, [groupInfoMap])

  // 监听消息变化，自动滚动到底部
  useEffect(() => {
    scrollToBottom()
  }, [currentMessages])

  // 滚动监听，检测是否滚动到顶部
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const { scrollTop } = target

    // 如果滚动到顶部（考虑一些误差范围），触发加载更多
    if (scrollTop <= 50 && hasMoreMessages && !isLoadingMore && !isMessagesLoading) {
      loadMoreMessages()
    }
  }, [hasMoreMessages, isLoadingMore, isMessagesLoading, loadMoreMessages])

  const handleSendMessage = async (content: string): Promise<void> => {
    if (!conversation || !content.trim()) {
      console.warn('无法发送消息: 缺少对话或内容')
      return
    }

    try {
      logPrint('发送文本消息:', content)

      // 获取 MessageManager 实例
      const messageManager = MessageManager.getInstance()

      // 构造文本消息参数
      const textParams: TextParams = {
        conversationId: conversation.id,
        sessionType: conversation.session_type,
        content: content.trim()
      }

      // 如果是私聊，需要设置接收者ID
      if (conversation.session_type === SessionType.Direct) {
        // 获取当前用户ID
        const currentUserId = tabStateManager.getUserId()

        // 从参与者中找到非当前用户的参与者作为接收者
        const targetParticipant = conversation.participants?.find(p =>
          p.id !== currentUserId
        )

        if (targetParticipant) {
          textParams.receiverId = targetParticipant.id
          textParams.receiverHandle = targetParticipant.handle
        } else {
          console.warn('私聊中找不到目标接收者')
          return
        }
      }

      // 检查是否需要自动翻译发送的消息
      const translationSettings = getTranslationSettings(conversation.id)
      if (translationSettings.autoTranslateOutgoing) {
        try {
          ToastUtils.showLoading(true, '正在翻译发送的消息...')
          const result = await autoTranslateMessage(content.trim(), translationSettings, false)
          if (result) {
            textParams.translateContent = result.translated_content
          }
        } catch (error) {
          // 不影响主流程，只是警告
          console.warn('自动翻译发送的消息失败:', error)
        } finally {
          ToastUtils.showLoading(false)
        }
      }

      // 发送消息
      const success = await messageManager.sendText(textParams)

      if (success) {
        console.log('文本消息发送成功')
        // 消息已经在 MessageManager 中自动添加到本地状态，无需手动更新
      } else {
        console.error('文本消息发送失败')
        ToastUtils.showToastFail('消息发送失败')
      }
    } catch (error) {
      console.error('发送消息时出错:', error)
      ToastUtils.showToastFail('消息发送失败')
    }
  }

  const handleSendImage = async (): Promise<void> => {
    if (!conversation) {
      ToastUtils.showToastFail('无法发送图片：缺少对话信息')
      return
    }

    if (isUploadingFile.current) {
      ToastUtils.showToastFail('图片上传中，请稍后再试')
      return
    }

    isUploadingFile.current = true
    ToastUtils.showLoading(true, '图片上传中...')

    try {
      // 选择并上传图片，获取 Attachment 对象
      const result = await selectAndUploadImageWithAttachment(conversation.id)
      if (!result) {
        return // 用户取消选择或上传失败
      }

      // 获取 MessageManager 实例
      const messageManager = MessageManager.getInstance()

      // 直接使用 Attachment 对象构造图片消息参数
      const imageParams: ImageParams = {
        conversationId: conversation.id,
        sessionType: conversation.session_type,
        attachment: result // 直接传入完整的 Attachment 对象
      }

      logPrint('发送图片消息参数:', imageParams)
      logPrint('完整的 Attachment 对象:', result)

      // 如果是私聊，需要设置接收者ID
      if (conversation.session_type === SessionType.Direct) {
        const currentUserId = tabStateManager.getUserId()
        const targetParticipant = conversation.participants?.find(p => p.id !== currentUserId)

        if (targetParticipant) {
          imageParams.receiverId = targetParticipant.id
          imageParams.receiverHandle = targetParticipant.handle
        } else {
          ToastUtils.showToastFail('私聊中找不到目标接收者')
          return
        }
      }

      // 发送图片消息
      const success = await messageManager.sendImage(imageParams)

      if (success) {
        console.log('图片消息发送成功')
        ToastUtils.showToastSuccess('图片发送成功')
      } else {
        ToastUtils.showToastFail('图片发送失败')
      }
    } catch (error) {
      console.error('发送图片时出错:', error)
      ToastUtils.showToastFail('图片发送失败')
    } finally {
      isUploadingFile.current = false
      ToastUtils.showLoading(false)
    }
  }

  /**
   * 处理消息右键点击
   */
  const handleMessageContextMenu = (e: React.MouseEvent, messageId: string) => {
    e.preventDefault()
    setContextMenu({
      messageId,
      position: { x: e.clientX, y: e.clientY }
    })
  }

  /**
   * 关闭右键菜单
   */
  const closeContextMenu = () => {
    setContextMenu(null)
  }

  /**
   * 处理置顶消息
   */
  const handlePinMessage = async (messageId: string) => {
    if (conversation.session_type !== SessionType.Group) {
      toast.error('只有群聊才可以置顶消息')
      return
    }

    const groupItem = useGroupStore.getState().groupMap.get(conversation.id)
    if (!groupItem?.id) {
      toast.error('群组信息不完整')
      return
    }

    await pinGroupMessage(conversation.id, groupItem.id, messageId)
  }

  /**
   * 处理翻译消息
   */
  const handleTranslateMessage = async (messageId: string) => {
    const targetMessage = currentMessages.find((m: MessageInfo) => m.message_id === messageId)
    if (!targetMessage) {
      toast.error('未找到消息')
      return
    }

    logPrint('翻译消息:', targetMessage)

    try {
      // 更新消息的翻译状态
      const { updateMessage } = useChatStore.getState()

      if (targetMessage.decryptedContentData?.showTranslate) {
        return;
      } else {
        // 获取翻译设置
        const translationSettings = getTranslationSettings(conversation.id)
        const messageContent = targetMessage.decryptedContentData?.content

        if (!messageContent) {
          toast.error('消息内容为空，无法翻译')
          return
        }

        // 调用翻译API
        const result = await translateText(
          messageContent,
          translationSettings.incomingTargetLanguage
        )

        if (result) {
          updateMessage(conversation.id, messageId, {
            decryptedContentData: {
              ...targetMessage.decryptedContentData,
              showTranslate: true,
              translateContent: result.translated_content
            }
          })
          toast.success('翻译完成')
        } else {
          toast.error('翻译失败，请重试')
        }
      }
    } catch (error) {
      console.error('翻译消息失败:', error)
      toast.error('翻译失败，请重试')
    }
  }

  const handleSendFile = async (): Promise<void> => {
    if (!conversation) {
      ToastUtils.showToastFail('无法发送文件：缺少对话信息')
      return
    }

    if (isUploadingFile.current) {
      ToastUtils.showToastFail('文件上传中，请稍后再试')
      return
    }

    isUploadingFile.current = true
    ToastUtils.showLoading(true, '文件上传中...')

    try {
      // 选择并上传文件，获取 Attachment 对象
      const attachment = await selectAndUploadDocumentWithAttachment(conversation.id)
      if (!attachment) {
        return // 用户取消选择或上传失败
      }

      // 直接使用 Attachment 对象构造文件消息参数
      const fileParams: FileParams = {
        conversationId: conversation.id,
        sessionType: conversation.session_type,
        attachment: attachment // 直接传入完整的 Attachment 对象
      }

      logPrint('发送文件消息参数:', fileParams)
      logPrint('完整的 Attachment 对象:', attachment)

      // 如果是私聊，需要设置接收者ID
      if (conversation.session_type === SessionType.Direct) {
        const currentUserId = tabStateManager.getUserId()
        const targetParticipant = conversation.participants?.find(p => p.id !== currentUserId)

        if (targetParticipant) {
          fileParams.receiverId = targetParticipant.id
          fileParams.receiverHandle = targetParticipant.handle
        } else {
          ToastUtils.showToastFail('私聊中找不到目标接收者')
          return
        }
      }

      // 发送文件消息
      const success = await MessageManager.getInstance().sendFile(fileParams)

      if (success) {
        console.log('文件消息发送成功')
        ToastUtils.showToastSuccess('文件发送成功')
      } else {
        ToastUtils.showToastFail('文件发送失败')
      }
    } catch (error) {
      console.error('发送文件时出错:', error)
      ToastUtils.showToastFail('文件发送失败')
    } finally {
      isUploadingFile.current = false
      ToastUtils.showLoading(false)
    }
  }

  // 发送 @ 消息
  const handleSendAtMessage = async (content: string): Promise<void> => {
    if (!conversation) {
      ToastUtils.showToastFail('无法发送 @ 消息：缺少对话信息')
      return
    }

    try {
      logPrint('发送 @ 消息:', content, selectedAtUsers)

      // 检查是否 @所有人
      const isAll = selectedAtUsers.some(user => user.id === 'all')

      // 转换用户列表格式为 GroupMember[]
      const userList = selectedAtUsers
        .filter(user => user.id !== 'all')
        .map(user => ({
          id: user.id,
          nickname: user.nickname,
          avatar: user.avatar || '',
          handle: user.handle,
          handle_rand: ('handle_rand' in user ? user.handle_rand : user.handle), // 使用默认值
          role: ('role' in user ? user.role : 'member') as 'owner' | 'admin' | 'member' // 默认为 member
        }))

      // 构造 @ 消息参数
      const textAtParams: TextAtParams = {
        conversationId: conversation.id,
        sessionType: conversation.session_type,
        content: content,
        isAll: isAll,
        userList: userList
      }

      // 检查是否需要自动翻译发送的消息
      const translationSettings = getTranslationSettings(conversation.id)
      if (translationSettings.autoTranslateOutgoing) {
        try {
          ToastUtils.showLoading(true, '正在翻译发送的消息...')
          const result = await autoTranslateMessage(content, translationSettings, false)
          if (result) {
            textAtParams.translateContent = result.translated_content
          }
        } catch (error) {
          // 不影响主流程，只是警告
          console.warn('自动翻译发送的消息失败:', error)
        } finally {
          ToastUtils.showLoading(false)
        }
      }

      // 发送消息
      const success = await MessageManager.getInstance().sendTextAt(textAtParams)

      if (success) {
        console.log('@ 消息发送成功')
      } else {
        console.error('@ 消息发送失败')
        ToastUtils.showToastFail('@ 消息发送失败')
      }
    } catch (error) {
      console.error('发送 @ 消息时出错:', error)
      ToastUtils.showToastFail('@ 消息发送失败')
    }
  }


  // 处理输入框内容变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setMessageText(value)

    // 只在群组聊天中检测 @，且用户有发言权限时才启用 @ 功能
    if (conversation.session_type !== SessionType.Group || !canSendMessage) {
      return
    }

    // 检测是否输入了 @
    const atMatch = value.match(/@([^@\s]*)$/)
    if (atMatch) {
      const searchTerm = atMatch[1]
      setAtSearchTerm(searchTerm)

      // 关闭 emoji 选择器
      setShowEmojiPicker(false)

      // 计算弹出框位置
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect()
        setAtInputPosition({
          x: rect.left,
          y: rect.top
        })
      }

      setShowAtSelector(true)
    } else {
      setShowAtSelector(false)
      setAtSearchTerm('')
    }
  }

  // 处理选择 @ 用户
  const handleSelectAtUser = (user: AtUser) => {
    // 替换输入框中的 @ 部分
    const atMatch = messageText.match(/@([^@\s]*)$/)
    if (atMatch) {
      const beforeAt = messageText.substring(0, messageText.lastIndexOf('@'))
      const userName = user.id === 'all' ? '所有人' : user.nickname
      const newValue = beforeAt + `@${userName} `
      setMessageText(newValue)

      // 记录选中的用户
      if (!selectedAtUsers.find(u => u.id === user.id)) {
        setSelectedAtUsers(prev => [...prev, user])
      }
    }

    setShowAtSelector(false)
    setAtSearchTerm('')

    // 重新聚焦输入框
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  // 关闭 @ 选择器
  const closeAtSelector = () => {
    setShowAtSelector(false)
    setAtSearchTerm('')
  }

  // 处理 emoji 选择
  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji
    setMessageText(prev => prev + emoji)
    setShowEmojiPicker(false)

    // 重新聚焦输入框
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  // 切换 emoji 选择器显示状态
  const toggleEmojiPicker = () => {
    // 关闭其他弹出框
    setShowAtSelector(false)
    setAtSearchTerm('')

    // 如果要显示 emoji 选择器，先计算位置
    if (!showEmojiPicker && emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect()
      const pickerWidth = 350
      const pickerHeight = 400

      // 计算水平位置，确保不超出屏幕左右边界
      let x = rect.left + rect.width / 2
      const halfPickerWidth = pickerWidth / 2

      if (x - halfPickerWidth < 10) {
        // 如果左边超出，调整到最小边距
        x = halfPickerWidth + 10
      } else if (x + halfPickerWidth > window.innerWidth - 10) {
        // 如果右边超出，调整到最大边距
        x = window.innerWidth - halfPickerWidth - 10
      }

      // 计算垂直位置，确保在按钮上方有足够空间
      let y = rect.top
      if (y - pickerHeight - 10 < 0) {
        // 如果上方空间不够，显示在按钮下方
        y = rect.bottom + 10
      }

      setEmojiButtonPosition({ x, y })
    }

    setShowEmojiPicker(prev => !prev)
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (messageText.trim()) {
      // 检查是否包含 @ 用户
      if (selectedAtUsers.length > 0 && conversation.session_type === SessionType.Group) {
        await handleSendAtMessage(messageText.trim())
      } else {
        await handleSendMessage(messageText.trim())
      }
      setMessageText('')
      setSelectedAtUsers([])
    }

    // 编写 测试案例，循环发送 500 条消息，消息内容为 测试xx, 延迟300毫秒发送一次
    // try {
    //   for (let i = 550; i < 1000; i++) {
    //     handleSendMessage(`测试${i}`)
    //     await new Promise(resolve => setTimeout(resolve, 50))
    //   }
    // } catch (error) {
    //   console.error('发送消息时出错:', error)
    // }
  }

  return (
    <div className="flex-1 flex bg-white">
      {/* 主聊天区域 */}
      <div className="flex-1 flex flex-col">
        {/* 聊天头部 */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                {conversation.avatar ? (
                  <img
                    src={conversation.avatar}
                    alt={conversation.title || '对话'}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-white font-medium">
                      {conversation.title ? conversation.title.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                )}
                {conversation.session_type === 'group' && (
                  <div className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-xs rounded-full px-1 min-w-4 h-4 flex items-center justify-center border-2 border-white">
                    {conversation.participants?.length || 0}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{conversation.title || '未知对话'}</h3>
                {conversation.session_type === 'group' ? (
                  <p className="text-sm text-gray-500">
                    {conversation.participants?.length || 0} 位成员
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {conversation.remark || conversation.alias || '私聊'}
                  </p>
                )}
              </div>
            </div>

            {/* 右侧操作按钮 */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowTranslationSettings(true)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="翻译设置"
              >
                <img src={iconTranslate} alt="翻译设置" className="w-8 h-8" />
              </button>

              {conversation.session_type === 'group' && (
                <button
                  onClick={() => setShowGroupSettings(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="群组设置"
                >
                  <img src={iconGroupSettings} alt="群组设置" className="w-8 h-8" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 置顶消息 */}
        <PinnedMessage
          conversationId={conversation.id}
          sessionType={conversation.session_type}
        />

        {/* 消息列表 */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-6 space-y-4"
          onScroll={handleScroll}
        >
          {/* 加载更多消息的提示 */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-sm text-gray-500">加载更多消息...</span>
            </div>
          )}

          {/* 没有更多消息的提示 */}
          {!hasMoreMessages && currentMessages.length > 0 && (
            <div className="flex items-center justify-center py-4">
              <span className="text-xs text-gray-400">没有更多消息了</span>
            </div>
          )}

          {isMessagesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : currentMessages.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">暂无聊天记录，请开始对话。</p>
              </div>
            </div>
          ) : (
            currentMessages.map((message: any) => {
              // 格式化时间
              const formattedTime = formatHumanTime(message.receiver_time ?? message.message_id_time)

              // 判断是否是当前用户发送的消息
              const isOwnMessage = message.senderName === '我'

              const messageType = message.messageType as MessageType;

              // 检查是否为系统消息
              const isSystemMessage = messageType?.includes("GROUP") ?? false;

              // 系统消息特殊处理（居中显示）
              if (isSystemMessage) {
                return (
                  <div key={message.message_id} className="mb-4">
                    <MessageContent message={message} isOwnMessage={isOwnMessage} />
                  </div>
                );
              }

              // 普通消息处理
              return (
                <div
                  key={message.message_id}
                  className={`flex items-start space-x-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  {/* 左侧头像（非自己的消息） */}
                  {!isOwnMessage && (
                    <div className="flex-shrink-0">
                      {message.senderAvatar ? (
                        <img
                          src={message.senderAvatar}
                          alt={message.senderName || '用户'}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-white text-xs font-medium">
                            {(message.senderName || message.sender_id).charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={`max-w-3xl ${isOwnMessage ? 'ml-12' : 'mr-12'}`}>
                    {/* 发送者名称（非自己的消息显示） */}
                    {!isOwnMessage && (
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          {message.senderName || message.sender_id}
                        </span>
                        {message.senderRemark && (
                          <span className="text-xs text-gray-500">({message.senderRemark})</span>
                        )}
                      </div>
                    )}

                    {/* 消息内容 */}
                    <div onContextMenu={(e) => handleMessageContextMenu(e, message.message_id)}>
                      <MessageContent message={message} isOwnMessage={isOwnMessage} />
                    </div>

                    {/* 时间戳 */}
                    <div className={`text-xs mt-1 ${isOwnMessage ? 'text-right text-gray-400' : 'text-left text-gray-500'
                      }`}>
                      {formattedTime}
                    </div>
                  </div>

                  {/* 右侧头像（自己的消息） */}
                  {isOwnMessage && (
                    <div className="flex-shrink-0">
                      {message.senderAvatar ? (
                        <img
                          src={message.senderAvatar}
                          alt="我"
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-white text-xs font-medium">我</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 消息输入框或禁言提示 */}
        {canSendMessage ? (
          <div className="border-t border-gray-200 px-6 py-4 bg-white">
            <form onSubmit={handleSubmit}>
              <div className="flex items-center space-x-4">
                {/* 功能按钮 */}
                <div className="flex items-center space-x-2">
                  {/* 发送图片按钮 */}
                  {canSendImage && (
                    <button
                      type="button"
                      onClick={handleSendImage}
                      disabled={isUploadingFile.current}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="发送图片"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}

                  {/* 发送文件按钮 */}
                  {canSendFile && (
                    <button
                      type="button"
                      onClick={handleSendFile}
                      disabled={isUploadingFile.current}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="发送文件"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  )}

                  {/* Emoji 按钮 */}
                  <button
                    ref={emojiButtonRef}
                    type="button"
                    onClick={toggleEmojiPicker}
                    className={`p-2 rounded-lg transition-colors ${showEmojiPicker
                      ? 'text-blue-600 bg-blue-100'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                    title="表情"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                </div>

                {/* 输入框 */}
                <div className="flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={messageText}
                    onChange={handleInputChange}
                    placeholder="Message"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* 发送按钮 */}
                <button
                  type="submit"
                  disabled={!messageText.trim()}
                  className="p-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        ) : conversation.session_type === SessionType.Group ? (
          /* 群组禁言提示 */
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="h-20 flex items-center justify-center">
              <div className="text-gray-500 text-sm">
                当前群禁止发言，请等待管理员解除禁言
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* 群组设置侧边栏 - 悬浮在右侧 */}
      {conversation.session_type === 'group' && showGroupSettings && (
        <>
          {/* 遮罩层 */}
          <div
            className="absolute inset-0 bg-black bg-opacity-10 z-40"
            onClick={() => setShowGroupSettings(false)}
          />
          {/* 设置面板 */}
          <div className="absolute top-0 right-0 h-full z-50">
            <GroupSettings
              conversationId={conversation.id}
              onClose={() => setShowGroupSettings(false)}
            />
          </div>
        </>
      )}

      {/* 翻译设置对话框 */}
      <TranslationSettingsDialog
        conversationId={conversation.id}
        isOpen={showTranslationSettings}
        onClose={() => setShowTranslationSettings(false)}
      />

      {/* 消息右键菜单 */}
      {contextMenu && (
        <MessageContextMenu
          message={currentMessages.find((m: MessageInfo) => m.message_id === contextMenu.messageId)!}
          sessionType={conversation.session_type}
          isShowPin={isOwner}
          position={contextMenu.position}
          onClose={closeContextMenu}
          onPin={handlePinMessage}
          onTranslate={handleTranslateMessage}
          onCopy={(content) => {
            navigator.clipboard.writeText(content)
            ToastUtils.showToastSuccess('复制成功')
          }}
        />
      )}

      {/* @ 用户选择弹出框 */}
      {showAtSelector && conversation.session_type === 'group' && (
        <>
          {/* 点击背景关闭 */}
          <div
            className="fixed inset-0 z-40"
            onClick={closeAtSelector}
          />
          <AtSelectorPopup
            conversation={conversation}
            searchTerm={atSearchTerm}
            position={atInputPosition}
            onSelectUser={handleSelectAtUser}
            onClose={closeAtSelector}
          />
        </>
      )}

      {/* Emoji 选择器弹出框 */}
      {showEmojiPicker && (
        <>
          {/* 点击背景关闭 */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowEmojiPicker(false)}
          />
          <div
            className="fixed z-50 shadow-lg rounded-lg overflow-hidden"
            style={{
              left: emojiButtonPosition.x - 175, // 弹框宽度350的一半，使其居中对齐按钮
              top: emojiButtonPosition.y > 410
                ? emojiButtonPosition.y - 410  // 显示在按钮上方
                : emojiButtonPosition.y, // 显示在按钮下方
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiSelect}
              width={350}
              height={400}
              searchDisabled={false}
              skinTonesDisabled={false}
              previewConfig={{
                showPreview: false
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}
